import type { Character } from "../types/character";
import type {
  HorrorRaceState,
  RoundDrawRecord,
  RoundTurnDraw,
} from "../types/game-state";
import { isFewPlayerVariant } from "./player-count-rules";

/**
 * M1: helper used by applyDrawCoins + applyForfeitTurn to record one turn's
 * draw on the current race's history. If the last record matches the current
 * (race, round) tuple, append to it; otherwise create a new record. Pure —
 * does not read `state.currentRaceDrawHistory` (caller passes it explicitly
 * so the caller can also use this from initial-state contexts).
 *
 * Defensive: callers should read `state.currentRaceDrawHistory ?? []` to
 * handle in-flight pre-v7 game states that lack the field (R3 mitigation).
 */
export function appendDraw(
  history: readonly RoundDrawRecord[],
  state: HorrorRaceState,
  draw: RoundTurnDraw,
): readonly RoundDrawRecord[] {
  const expectedTurns =
    state.turnOrder.length *
    (isFewPlayerVariant(state.config.playerCount) ? 2 : 1);

  const last = history[history.length - 1];
  const matchesCurrent =
    last != null &&
    last.raceNumber === state.raceNumber &&
    last.roundNumber === state.round;

  if (matchesCurrent) {
    const nextDraws = [...last.draws, draw];
    const hadForfeit = last.hadForfeit || draw.coins === null;
    const updated: RoundDrawRecord = {
      ...last,
      draws: nextDraws,
      hadForfeit,
      isComplete: !hadForfeit && nextDraws.length === last.expectedTurns,
    };
    return [...history.slice(0, -1), updated];
  }

  const fresh: RoundDrawRecord = {
    raceNumber: state.raceNumber,
    roundNumber: state.round,
    draws: [draw],
    hadForfeit: draw.coins === null,
    isComplete: false,
    expectedTurns,
  };
  return [...history, fresh];
}

/**
 * Defensive accessor — returns the history array, defaulting to [] if the
 * field is absent (pre-v7 serialized state in flight at deploy time). Use
 * this everywhere instead of reading `state.currentRaceDrawHistory` directly.
 */
export function getCurrentRaceDrawHistory(
  state: HorrorRaceState,
): readonly RoundDrawRecord[] {
  return state.currentRaceDrawHistory ?? [];
}

/**
 * Helper for appliers that need the playerIndex at the moment of the action:
 * the currently-acting player as recorded by turnOrder + currentTurnIndex.
 */
export function getActingPlayerIndex(state: HorrorRaceState): number {
  return state.turnOrder[state.currentTurnIndex];
}

export function getCurrentDrawPass(state: HorrorRaceState): 1 | 2 {
  const pass = state.drawPass ?? 1;
  return pass === 2 ? 2 : 1;
}

export type { Character }; // re-export convenience for adjacent modules
